import type { Thread } from '@store/reducers/threads'
import type React from 'react'
import { useRef } from 'react'
import Draggable, {
  type DraggableData,
  type DraggableEvent,
} from 'react-draggable'

import { useDrag } from '@/hooks'
import { clsJoin, isDragMoved } from '@/utils'
import { useThreadAccess, useThreadDrop, useThreadTarget } from '../../hooks'
import { getEventOffset } from '../../utils'
import css from './styles.module.css'

interface Props extends React.PropsWithChildren {
  thread: Thread
  open: boolean
  scale: number
  /** Selector of the part that does not start a drag — the open card. */
  cancel: string
}

export const ThreadDraggable: React.FC<Props> = ({
  children,
  thread,
  open,
  scale,
  cancel,
}) => {
  const { uuid, commentable, offset } = thread

  const { onDrop } = useThreadDrop(uuid, commentable)

  const { trackTarget, resetTarget } = useThreadTarget()

  const { onDrag, onStop: stopHandler, isDragging } = useDrag(onDrop, offset)

  const canMove = useThreadAccess(thread.owner, 'move')

  // the mouseup of a drag still clicks the bubble, which would toggle the
  // thread; a press that stays under the threshold is a click and leaves the
  // thread where it was. The flag is reset on every press, not in onStart:
  // react-draggable skips onStart for a press on the card (`cancel`), and a
  // flag left over from the last drag would swallow the card's clicks
  const dragged = useRef(false)

  const onMouseDownCapture = () => {
    dragged.current = false
  }

  const onDragMove = (event: DraggableEvent, data: DraggableData) => {
    if (!isDragMoved(data, offset, scale)) {
      return
    }

    dragged.current = true
    onDrag()
    trackTarget(getEventOffset(event as React.MouseEvent))
  }

  // under the threshold the bubble snaps back: the position is controlled
  const onStop = (event: DraggableEvent, data: DraggableData) => {
    resetTarget()

    if (!dragged.current) {
      return
    }

    stopHandler(event, data)
  }

  const onClickCapture = (event: React.MouseEvent) => {
    if (dragged.current) {
      event.stopPropagation()
    }
  }

  const className = clsJoin(
    css.thread_draggable,
    isDragging && css.dragging,
    open && css.open
  )

  const nodeRef = useRef<HTMLDivElement>(null)

  return (
    <Draggable
      nodeRef={nodeRef}
      disabled={!canMove}
      cancel={cancel}
      scale={scale}
      position={offset}
      onDrag={onDragMove}
      onStop={onStop}
      grid={[3, 3]}
    >
      <div
        ref={nodeRef}
        data-jump-id={uuid}
        className={className}
        onMouseDownCapture={onMouseDownCapture}
        onClickCapture={onClickCapture}
      >
        {children}
      </div>
    </Draggable>
  )
}
